import { useQuery } from '@tanstack/react-query';

import type { Lens, LensCustomView } from '~/types/leLens';

import { lensDefinitionChanges } from '../lensDefinitionChanges';
import { type LensProposal, lensProposalKey } from '../lensProposal';

/** svc-reporting keeps a staged change for an hour; an older one can no longer be saved, so it is not previewed. */
export const CUSTOM_VIEW_PREVIEW_MAX_AGE_MS = 60 * 60 * 1000;

/**
 * The custom view an unsaved askRichard change would give this Lens, or undefined when nothing
 * should be previewed: no pending change, a change to something else, or one that is saved,
 * discarded, out of date or expired. The Lens page draws this view in place of the saved one
 * until the reader chooses Save or Discard, so they see the change on live data before saving it.
 */
export function pendingCustomViewPreview(
  lens: Pick<Lens, 'id' | 'version' | 'spec'> | undefined,
  proposal: LensProposal | null | undefined,
  now = Date.now(),
): LensCustomView | undefined {
  if (!lens || !proposal || proposal.purpose === 'review_proposal' || proposal.status || proposal.lensId !== lens.id) {
    return undefined;
  }
  if (!proposal.candidateId || proposal.baseVersion !== lens.version) {
    return undefined;
  }
  if (!(now - proposal.createdAt < CUSTOM_VIEW_PREVIEW_MAX_AGE_MS)) {
    return undefined;
  }
  const next = proposal.definition?.spec.customView;
  if (!next || !lensDefinitionChanges(lens.spec.customView, next).length) {
    return undefined;
  }
  return next;
}

/**
 * Reads the latest proposal LensProposalPreview keeps in the query cache for this Lens (it writes
 * it there when the proposal appears, and again when it is saved or discarded).
 */
export function usePendingCustomViewPreview(lens: Pick<Lens, 'id' | 'version' | 'spec'> | undefined) {
  const { data } = useQuery<LensProposal | null>({
    queryKey: lensProposalKey(lens?.id ?? ''),
    queryFn: () => null,
    enabled: false,
  });
  return pendingCustomViewPreview(lens, data);
}
